import { Suspense, lazy } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { Menu } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { NetworkStatusBanner } from "@/components/NetworkStatusBanner";
import { CommandPalette } from "@/components/CommandPalette";
import { SkipLink } from "@/components/SkipLink";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { STALE_TIMES, GC_TIMES } from "@/hooks/useOptimizedQuery";
import ProtectedRoute from "./components/ProtectedRoute";
import { AdminGuard } from "./components/AdminGuard";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { MFAEnforcementGuard } from "./components/MFAEnforcementGuard";
import { TimeOffGuard } from "./components/TimeOffGuard";

// Lazy-loaded pages for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const Index = lazy(() => import("./pages/Index"));
const MyReviews = lazy(() => import("./pages/MyReviews"));
const PeerReviewForm = lazy(() => import("./pages/PeerReviewForm"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const Templates = lazy(() => import("./pages/Templates"));
const TemplateCreator = lazy(() => import("./pages/TemplateCreator"));
const TemplatePreview = lazy(() => import("./pages/TemplatePreview"));
const Teams = lazy(() => import("./pages/Teams"));
const StaffMembers = lazy(() => import("./pages/StaffMembers"));
const StaffMemberProfile = lazy(() => import("./pages/StaffMemberProfile"));
const ReviewCycles = lazy(() => import("./pages/ReviewCycles"));
const CreateReviewCycle = lazy(() => import("./pages/CreateReviewCycle"));
const ReviewCycleDetail = lazy(() => import("./pages/ReviewCycleDetail"));
const StaffReviewForm = lazy(() => import("./pages/StaffReviewForm"));
const Settings = lazy(() => import("./pages/Settings"));
const PlanBilling = lazy(() => import("./pages/PlanBilling"));
const Support = lazy(() => import("./pages/Support"));
const ManagerFeedback = lazy(() => import("./pages/ManagerFeedback"));
const ViewEvaluations = lazy(() => import("./pages/ViewEvaluations"));
const Analytics = lazy(() => import("./pages/Analytics"));
const TeamCalendar = lazy(() => import("./pages/TeamCalendar"));
const RosterCalendar = lazy(() => import("./pages/RosterCalendar"));
const TimeOffSettings = lazy(() => import("./pages/TimeOffSettings"));
const LeavePolicy = lazy(() => import("./pages/LeavePolicy"));
const LeavePolicyBuilder = lazy(() => import("./pages/LeavePolicyBuilder"));
const Timesheets = lazy(() => import("./pages/Timesheets"));
const TimesheetDetail = lazy(() => import("./pages/TimesheetDetail"));
const LeaveReports = lazy(() => import("./pages/LeaveReports"));
const SurveyDesigner = lazy(() => import("./pages/SurveyDesigner"));
const PollDesigner = lazy(() => import("./pages/PollDesigner"));
const SurveyTemplateEditor = lazy(() => import("./pages/SurveyTemplateEditor"));
const SurveySchedule = lazy(() => import("./pages/SurveySchedule"));
const MySurveys = lazy(() => import("./pages/MySurveys"));
const SurveyResponse = lazy(() => import("./pages/SurveyResponse"));
const SurveyResults = lazy(() => import("./pages/SurveyResults"));
const SurveyResultsList = lazy(() => import("./pages/SurveyResultsList"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const MyTimeOff = lazy(() => import("./pages/MyTimeOff"));
const MyTimesheets = lazy(() => import("./pages/MyTimesheets"));
const Goals = lazy(() => import("./pages/Goals"));
const GoalDetail = lazy(() => import("./pages/GoalDetail"));
const TeamGoals = lazy(() => import("./pages/TeamGoals"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminSupportDetail = lazy(() => import("./pages/admin/AdminSupportDetail"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminActivityLog = lazy(() => import("./pages/admin/AdminActivityLog"));
const AdminHelpCenter = lazy(() => import("./pages/admin/AdminHelpCenter"));
const AdminHelpCenterEditor = lazy(() => import("./pages/admin/AdminHelpCenterEditor"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Training pages
const TrainingCourses = lazy(() => import("./pages/training/TrainingCourses"));
const TrainingCourseEditor = lazy(() => import("./pages/training/TrainingCourseEditor"));
const TrainingSchedules = lazy(() => import("./pages/training/TrainingSchedules"));
const TrainingScheduleNew = lazy(() => import("./pages/training/TrainingScheduleNew"));
const TrainingScheduleDetail = lazy(() => import("./pages/training/TrainingScheduleDetail"));
const MyTraining = lazy(() => import("./pages/training/MyTraining"));
const TrainingPlayer = lazy(() => import("./pages/training/TrainingPlayer"));

// Optimized QueryClient with better caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIMES.fast,
      gcTime: GC_TIMES.medium,
      retry: 2,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

const MobileHeader = () => {
  const isMobile = useIsMobile();
  if (!isMobile) return null;
  return (
    <header className="sticky top-0 z-40 flex h-12 items-center border-b border-border bg-background px-3">
      <SidebarTrigger className="h-9 w-9">
        <Menu className="h-5 w-5" />
      </SidebarTrigger>
    </header>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Sonner />
        <NetworkStatusBanner />
        <BrowserRouter>
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>}>
            <Routes>
              <Route path="/" element={<Landing />} />
               <Route path="/auth" element={<Auth />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <SidebarProvider>
                    <SkipLink />
                    <CommandPalette />
                    <div className="flex min-h-screen w-full">
                      <AppSidebar />
                      <div className="flex-1 flex flex-col min-w-0" role="region" aria-label="Main application">
                        <MobileHeader />
                        <ImpersonationBanner />
                        <main 
                          id="main-content" 
                          className="flex-1 p-4 md:p-6 bg-background overflow-auto" 
                          role="main"
                          aria-label="Page content"
                        >
                          <MFAEnforcementGuard>
                            <Routes>
                              <Route path="/dashboard" element={<Index />} />
                              <Route path="/my-reviews" element={<MyReviews />} />
                              <Route path="/my-profile" element={<MyProfile />} />
                              <Route path="/templates" element={<Templates />} />
                              <Route path="/templates/new" element={<TemplateCreator />} />
                              <Route path="/templates/draft/:draftId" element={<TemplateCreator />} />
                              <Route path="/templates/edit/:id" element={<TemplateCreator />} />
                              <Route path="/templates/:id" element={<TemplatePreview />} />
                              <Route path="/cycles" element={<ReviewCycles />} />
                              <Route path="/cycles/new" element={<CreateReviewCycle />} />
                              <Route path="/cycles/:id" element={<ReviewCycleDetail />} />
                              <Route path="/assignments/:id" element={<StaffReviewForm />} />
                              <Route path="/peer-review/:id" element={<PeerReviewForm />} />
                              <Route path="/teams" element={<Teams />} />
                              <Route path="/staff-members" element={<StaffMembers />} />
                              <Route path="/staff-members/:id" element={<StaffMemberProfile />} />
                              <Route path="/settings" element={<Settings />} />
                              <Route path="/plan-billing" element={<PlanBilling />} />
                              <Route path="/support" element={<Support />} />
                              <Route path="/manager-feedback" element={<ManagerFeedback />} />
                              <Route path="/view-evaluations" element={<ViewEvaluations />} />
                              <Route path="/analytics" element={<Analytics />} />
                              <Route path="/calendar" element={<TimeOffGuard><TeamCalendar /></TimeOffGuard>} />
                              <Route path="/roster" element={<TimeOffGuard><RosterCalendar /></TimeOffGuard>} />
                              <Route path="/time-off-settings" element={<TimeOffGuard><TimeOffSettings /></TimeOffGuard>} />
                              <Route path="/leave-policies" element={<TimeOffGuard><LeavePolicy /></TimeOffGuard>} />
                              <Route path="/leave-policies/new" element={<TimeOffGuard><LeavePolicyBuilder /></TimeOffGuard>} />
                              <Route path="/leave-policies/:id/edit" element={<TimeOffGuard><LeavePolicyBuilder /></TimeOffGuard>} />
                              <Route path="/leave-reports" element={<TimeOffGuard><LeaveReports /></TimeOffGuard>} />
                              <Route path="/timesheets" element={<TimeOffGuard><Timesheets /></TimeOffGuard>} />
                              <Route path="/timesheets/:id" element={<TimeOffGuard><TimesheetDetail /></TimeOffGuard>} />
                              <Route path="/survey-designer" element={<SurveyDesigner />} />
                              <Route path="/survey-designer/:id" element={<SurveyTemplateEditor />} />
                              <Route path="/poll-designer" element={<PollDesigner />} />
                              <Route path="/poll-designer/:id" element={<SurveyTemplateEditor />} />
                              <Route path="/survey-schedule" element={<SurveySchedule />} />
                              <Route path="/my-surveys" element={<MySurveys />} />
                              <Route path="/survey-response/:id" element={<SurveyResponse />} />
                              <Route path="/survey-results" element={<SurveyResultsList />} />
                              <Route path="/survey-results/:id" element={<SurveyResults />} />
                              <Route path="/help-center" element={<HelpCenter />} />
                              <Route path="/my-time-off" element={<TimeOffGuard><MyTimeOff /></TimeOffGuard>} />
                              <Route path="/my-timesheets" element={<TimeOffGuard><MyTimesheets /></TimeOffGuard>} />
                              <Route path="/goals" element={<Goals />} />
                              <Route path="/goals/:id" element={<GoalDetail />} />
                              <Route path="/team-goals" element={<TeamGoals />} />

                              {/* Training routes */}
                              <Route path="/training/courses" element={<TrainingCourses />} />
                              <Route path="/training/courses/:id/edit" element={<TrainingCourseEditor />} />
                              <Route path="/training/schedules" element={<TrainingSchedules />} />
                              <Route path="/training/schedules/new" element={<TrainingScheduleNew />} />
                              <Route path="/training/schedules/:id" element={<TrainingScheduleDetail />} />
                              <Route path="/my-training" element={<MyTraining />} />
                              <Route path="/my-training/:assignmentId" element={<TrainingPlayer />} />
                              
                              {/* Admin routes */}
                              <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
                              <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
                              <Route path="/admin/users/:id" element={<AdminGuard><AdminUserDetail /></AdminGuard>} />
                              <Route path="/admin/support" element={<AdminGuard><AdminSupport /></AdminGuard>} />
                              <Route path="/admin/support/:id" element={<AdminGuard><AdminSupportDetail /></AdminGuard>} />
                              <Route path="/admin/email-templates" element={<AdminGuard><AdminEmailTemplates /></AdminGuard>} />
                              <Route path="/admin/activity-log" element={<AdminGuard><AdminActivityLog /></AdminGuard>} />
                              <Route path="/admin/help-center" element={<AdminGuard><AdminHelpCenter /></AdminGuard>} />
                              <Route path="/admin/help-center/:id" element={<AdminGuard><AdminHelpCenterEditor /></AdminGuard>} />
                              <Route path="/admin/blog" element={<AdminGuard><AdminBlog /></AdminGuard>} />
                              <Route path="/admin/blog/:id" element={<AdminGuard><AdminBlogEditor /></AdminGuard>} />
                              
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </MFAEnforcementGuard>
                        </main>
                      </div>
                    </div>
                  </SidebarProvider>
                </ProtectedRoute>
              }
            />
             {/* Catch-all 404 for unauthenticated users */}
             <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
